import React from 'react';

// Gráficas simples en SVG/divs, sin librerías externas (app liviana y offline).
// Paleta categórica: colores distintos y legibles para cada segmento/barra.
export const PALETA = ['#7C3AED', '#DB2777', '#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'];

const Punto = ({ color }) => (
  <span style={{ width: '11px', height: '11px', borderRadius: '3px', backgroundColor: color, flexShrink: 0, display: 'inline-block' }} />
);

// Dona (gráfica circular). data: [{label, value, color}]
export function Dona({ data, size = 150, grosor = 24, centro1, centro2 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const r = (size - grosor) / 2;
  const C = 2 * Math.PI * r;
  const cx = size / 2;
  const cy = size / 2;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F3F4F6" strokeWidth={grosor} />
      {total > 0 && data.map((d, i) => {
        const seg = (d.value / total) * C;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={d.color} strokeWidth={grosor}
            strokeDasharray={`${seg} ${C - seg}`} strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`} />
        );
        offset += seg;
        return el;
      })}
      {centro1 != null && <text x={cx} y={cy - 1} textAnchor="middle" fontSize="18" fontWeight="700" fill="#111827">{centro1}</text>}
      {centro2 != null && <text x={cx} y={cy + 15} textAnchor="middle" fontSize="10" fill="#9CA3AF">{centro2}</text>}
    </svg>
  );
}

// Dona + leyenda (color, etiqueta, valor, %). data: [{label, value, color?}]
// totalLabel: si viene, agrega una fila de total debajo de la leyenda.
export function DonaConLeyenda({ data, formato = (n) => n, centro2, vacio = 'Sin datos.', totalLabel }) {
  const items = (data || []).filter((d) => d.value > 0).map((d, i) => ({ ...d, color: d.color || PALETA[i % PALETA.length] }));
  const total = items.reduce((s, d) => s + d.value, 0);
  if (total <= 0) return <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF' }}>{vacio}</p>;
  return (
    <div style={{ display: 'flex', gap: '18px', alignItems: 'center', flexWrap: 'wrap' }}>
      <Dona data={items} centro1={items.length} centro2={centro2} />
      <div style={{ flex: 1, minWidth: '200px' }}>
        {items.map((d, i) => {
          const pct = Math.round((d.value / total) * 100);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '13px' }}>
              <Punto color={d.color} />
              <span style={{ flex: 1, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
              <span style={{ color: '#374151', fontWeight: 600 }}>{formato(d.value)}</span>
              <span style={{ color: '#9CA3AF', width: '38px', textAlign: 'right' }}>{pct}%</span>
            </div>
          );
        })}
        {totalLabel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0 2px', marginTop: '4px', borderTop: '1px solid #E5E7EB', fontSize: '13.5px' }}>
            <span style={{ width: '10px' }} />
            <span style={{ flex: 1, color: '#111827', fontWeight: 700 }}>{totalLabel}</span>
            <span style={{ color: '#111827', fontWeight: 800 }}>{formato(total)}</span>
            <span style={{ color: '#9CA3AF', width: '38px', textAlign: 'right' }}>100%</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Barras horizontales (comparación): la barra más grande llena la fila.
// data: [{label, value, color?, extra?}]
export function BarrasH({ data, formato = (n) => n, color, vacio = 'Sin datos.' }) {
  const items = (data || []).map((d, i) => ({ ...d, color: d.color || color || PALETA[i % PALETA.length] }));
  const max = Math.max(...items.map((d) => d.value), 0);
  if (items.length === 0 || max <= 0) return <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF' }}>{vacio}</p>;
  return (
    <div>
      {items.map((d, i) => {
        const pct = Math.round((d.value / max) * 100);
        return (
          <div key={i} style={{ marginBottom: '9px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px', gap: '8px' }}>
              <span style={{ color: '#111827', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
              <span style={{ color: '#374151', fontWeight: 600, flexShrink: 0 }}>{formato(d.value)}{d.extra ? ` · ${d.extra}` : ''}</span>
            </div>
            <div style={{ height: '14px', backgroundColor: '#F3F4F6', borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(pct, 2)}%`, height: '100%', backgroundColor: d.color, borderRadius: '6px' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Barras apiladas costo + ganancia (para recuperación por categoría).
// data: [{label, costo, ganancia}]
export function BarrasApiladas({ data, formato = (n) => n, vacio = 'Sin datos.' }) {
  const max = Math.max(...(data || []).map((d) => d.costo + d.ganancia), 0);
  if (!data || data.length === 0 || max <= 0) return <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF' }}>{vacio}</p>;
  return (
    <div>
      {data.map((d, i) => {
        const total = d.costo + d.ganancia;
        const wTotal = Math.max(Math.round((total / max) * 100), 2);
        const wCosto = total > 0 ? (d.costo / total) * 100 : 0;
        const wGan = total > 0 ? (d.ganancia / total) * 100 : 0;
        return (
          <div key={i} style={{ marginBottom: '9px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px', gap: '8px' }}>
              <span style={{ color: '#111827', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
              <span style={{ color: '#374151', fontWeight: 600, flexShrink: 0 }}>{formato(total)}</span>
            </div>
            <div style={{ display: 'flex', width: `${wTotal}%`, height: '14px', borderRadius: '6px', overflow: 'hidden', minWidth: '4px' }}>
              <div style={{ width: `${wCosto}%`, height: '100%', backgroundColor: '#3B82F6' }} title={`Costo recuperado ${formato(d.costo)}`} />
              <div style={{ width: `${wGan}%`, height: '100%', backgroundColor: '#10B981' }} title={`Ganancia ${formato(d.ganancia)}`} />
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: '11px', color: '#6B7280' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Punto color="#3B82F6" /> Costo recuperado (reinvertir)</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Punto color="#10B981" /> Ganancia</span>
      </div>
    </div>
  );
}

// Barras verticales (columnas). data: [{label, value, color?}]
export function BarrasV({ data }) {
  const items = (data || []).map((d, i) => ({ ...d, color: d.color || PALETA[i % PALETA.length] }));
  const max = Math.max(...items.map((d) => d.value), 1);
  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', height: '130px', padding: '0 4px' }}>
      {items.map((d, i) => {
        const h = Math.round((d.value / max) * 92) + 4;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', marginBottom: '2px' }}>{d.value}</span>
            <div style={{ width: '100%', maxWidth: '56px', height: `${h}px`, backgroundColor: d.color, borderRadius: '6px 6px 0 0' }} />
            <span style={{ fontSize: '11px', color: '#6B7280', marginTop: '5px', textAlign: 'center', lineHeight: 1.2 }}>{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}
